/**
 * Calorie and macro targets. Deterministic math with hard safety rails;
 * the UI and AI explain these numbers but never override the floors.
 */
import { formatWeight, type WeightUnit } from './units';

export type Sex = 'female' | 'male' | 'other';
export type Activity = 'sedentary' | 'light' | 'moderate' | 'active' | 'very_active';

export const ACTIVITY: Record<Activity, { factor: number; label: string; hint: string }> = {
  sedentary: { factor: 1.2, label: 'Mostly sitting', hint: 'Desk job, little exercise' },
  light: { factor: 1.375, label: 'Lightly active', hint: 'Walks or light exercise 1–3 days/week' },
  moderate: { factor: 1.55, label: 'Moderately active', hint: 'Exercise 3–5 days/week' },
  active: { factor: 1.725, label: 'Very active', hint: 'Hard exercise 6–7 days/week' },
  very_active: { factor: 1.9, label: 'Athlete', hint: 'Physical job or twice-daily training' },
};

/** Energy in 1 kg of body weight change (approximation). */
export const KCAL_PER_KG = 7700;
export const CALORIE_FLOOR: Record<Sex, number> = { female: 1200, male: 1500, other: 1200 };
/** Never plan to lose more than 1% of body weight per week. */
export const MAX_WEEKLY_LOSS_FRACTION = 0.01;
export const MIN_HEALTHY_BMI = 18.5;
export const PACE_OPTIONS_KG = [0, 0.25, 0.5, 0.75, 1] as const;

export interface TargetInput {
  sex: Sex;
  birthYear: number;
  heightCm: number;
  activity: Activity;
  currentKg: number;
  goalKg: number | null;
  paceKgPerWeek: number;
  year?: number;
  /** Units for the explanation text only. */
  unit?: WeightUnit;
}

export interface MacroTargets {
  kcal: number;
  protein: number;
  carbs: number;
  fat: number;
}

export interface SuggestedTargets extends MacroTargets {
  bmr: number;
  tdee: number;
  direction: 'lose' | 'gain' | 'maintain';
  effectivePaceKgPerWeek: number;
  floorApplied: boolean;
  paceCapped: boolean;
  explanation: string[];
}

export function ageFromBirthYear(birthYear: number, year = new Date().getFullYear()): number {
  return Math.max(13, Math.min(110, year - birthYear));
}

/** Mifflin–St Jeor resting energy. "other" uses the midpoint of the two equations. */
export function bmrMifflin(sex: Sex, kg: number, cm: number, age: number): number {
  const base = 10 * kg + 6.25 * cm - 5 * age;
  if (sex === 'male') return base + 5;
  if (sex === 'female') return base - 161;
  return base - 78;
}

export function maxPaceKg(currentKg: number): number {
  return currentKg * MAX_WEEKLY_LOSS_FRACTION;
}

export function minHealthyGoalKg(heightCm: number): number {
  const m = heightCm / 100;
  return MIN_HEALTHY_BMI * m * m;
}

function round(n: number, step: number): number {
  return Math.round(n / step) * step;
}

export function suggestTargets(input: TargetInput): SuggestedTargets {
  const age = ageFromBirthYear(input.birthYear, input.year);
  const bmr = bmrMifflin(input.sex, input.currentKg, input.heightCm, age);
  const tdee = bmr * ACTIVITY[input.activity].factor;
  const explanation: string[] = [
    `Resting energy ≈ ${Math.round(bmr).toLocaleString('en-US')} kcal (Mifflin–St Jeor, age ${age}).`,
    `With your activity (${ACTIVITY[input.activity].label.toLowerCase()}), you burn ≈ ${Math.round(tdee).toLocaleString('en-US')} kcal/day.`,
  ];

  let direction: SuggestedTargets['direction'] = 'maintain';
  if (input.goalKg !== null && Math.abs(input.goalKg - input.currentKg) >= 0.5) {
    direction = input.goalKg < input.currentKg ? 'lose' : 'gain';
  }

  let pace = direction === 'maintain' ? 0 : Math.max(0, input.paceKgPerWeek);
  let paceCapped = false;
  if (direction === 'lose' && pace > maxPaceKg(input.currentKg)) {
    pace = maxPaceKg(input.currentKg);
    paceCapped = true;
    explanation.push('Pace capped at 1% of body weight per week to protect muscle and energy.');
  }
  if (direction === 'gain') pace = Math.min(pace, 0.5);

  const dailyDelta = (pace * KCAL_PER_KG) / 7;
  let kcal = direction === 'lose' ? tdee - dailyDelta : direction === 'gain' ? tdee + dailyDelta : tdee;
  if (direction !== 'maintain' && pace > 0) {
    const unit = input.unit ?? 'kg';
    explanation.push(
      `${direction === 'lose' ? 'Losing' : 'Gaining'} ${formatWeight(pace, unit, { decimals: unit === 'lb' ? 1 : 2 })}/week needs ≈ ${Math.round(dailyDelta)} kcal/day ${direction === 'lose' ? 'less' : 'more'}.`,
    );
  }

  let floorApplied = false;
  const floor = CALORIE_FLOOR[input.sex];
  if (kcal < floor) {
    kcal = floor;
    floorApplied = true;
    explanation.push(`Raised to the ${floor.toLocaleString('en-US')} kcal minimum — going lower isn't recommended without a clinician.`);
  }
  kcal = round(kcal, 10);

  // Protein: ~1.4 g/kg body weight while losing (1.2 otherwise), capped at 35% of energy.
  const proteinPerKg = direction === 'lose' ? 1.4 : 1.2;
  const protein = Math.min(round(input.currentKg * proteinPerKg, 5), round((kcal * 0.35) / 4, 5));
  const fat = round((kcal * 0.3) / 9, 1);
  const carbs = Math.max(50, round((kcal - protein * 4 - fat * 9) / 4, 5));
  explanation.push(`Protein ${protein} g keeps you full and protects muscle; fat ~30% of energy; carbs fill the rest.`);

  return {
    kcal,
    protein,
    carbs,
    fat,
    bmr: Math.round(bmr),
    tdee: Math.round(tdee),
    direction,
    effectivePaceKgPerWeek: pace,
    floorApplied,
    paceCapped,
    explanation,
  };
}

/** Same two-way sync the existing app offers: calories ↔ macro grams ↔ percents. */
export function macroPercents(t: MacroTargets): { protein: number; carbs: number; fat: number } {
  const total = t.protein * 4 + t.carbs * 4 + t.fat * 9;
  if (total <= 0) return { protein: 0, carbs: 0, fat: 0 };
  return {
    protein: Math.round(((t.protein * 4) / total) * 100),
    carbs: Math.round(((t.carbs * 4) / total) * 100),
    fat: Math.round(((t.fat * 9) / total) * 100),
  };
}

export function gramsFromPercents(kcal: number, pct: { protein: number; carbs: number; fat: number }): MacroTargets {
  return {
    kcal,
    protein: Math.round((kcal * pct.protein) / 100 / 4),
    carbs: Math.round((kcal * pct.carbs) / 100 / 4),
    fat: Math.round((kcal * pct.fat) / 100 / 9),
  };
}

export interface TargetValidation {
  ok: boolean;
  kcal: number;
  message: string | null;
}

export function validateCalorieTarget(kcal: number, sex: Sex): TargetValidation {
  const floor = CALORIE_FLOOR[sex];
  if (!Number.isFinite(kcal)) return { ok: false, kcal: floor, message: 'Enter a calorie target.' };
  if (kcal < floor) {
    return {
      ok: false,
      kcal: floor,
      message: `Targets below ${floor.toLocaleString('en-US')} kcal aren't supported. Talk with a clinician if you've been advised to eat less.`,
    };
  }
  if (kcal > 6000) return { ok: false, kcal: 6000, message: 'That target looks too high — 6,000 kcal is the maximum.' };
  return { ok: true, kcal: Math.round(kcal), message: null };
}
